import type { HealthcareService } from '@medplum/fhirtypes';

import { Grid, Stack, Tag, Text } from '@eluve/components';

import {
  CodeSelectInput,
  IntegerInput,
  StringInput,
} from '../../components/inputs';
import { useFhirEditState } from '../../core/FhirEditScope';
import type { FhirView } from '../../core/view-contract';

import {
  APPOINTMENT_TYPE_SYSTEM,
  type SchedulingParameters,
  schedulingParameters,
  withSchedulingParameters,
} from './scheduling';

// Radix Select items can't carry an empty-string value.
const NO_CLASSIFICATION = 'none';

const CLASSIFICATION_OPTIONS = [
  { value: NO_CLASSIFICATION, label: 'None' },
  { value: 'new-patient', label: 'First visit' },
  { value: 'returning', label: 'Follow-up' },
] as const;

function withClassification(
  data: HealthcareService,
  code: string,
): HealthcareService {
  if (code === NO_CLASSIFICATION) return { ...data, type: undefined };
  return {
    ...data,
    type: [{ coding: [{ system: APPOINTMENT_TYPE_SYSTEM, code }] }],
  };
}

export const HealthcareServiceEditView: FhirView<HealthcareService> = ({
  data,
}) => {
  const { current, setCurrent, dirty } = useFhirEditState(data);
  const parameters = schedulingParameters(current);

  const setParameter = (
    name: keyof SchedulingParameters,
    value: number | undefined,
  ) =>
    setCurrent(
      withSchedulingParameters(current, { ...parameters, [name]: value }),
    );

  return (
    // Chrome rule: wears the card chrome of the read variant it replaces.
    <Stack
      gap={3}
      data-view="healthcare-service-edit"
      className="rounded-lg border border-border-primary bg-bg-primary p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" justify="between">
        <Text inline size="sm" weight="semibold">
          Service
        </Text>
        {dirty && <Tag pill subtle size="xs" color="warning" label="Edited" />}
      </Stack>
      <Grid columns={1} className="gap-x-6 gap-y-3 sm:grid-cols-2">
        <StringInput
          label="Name"
          value={current.name}
          placeholder="e.g. Initial Consultation"
          onChange={(next) => setCurrent({ ...current, name: next })}
        />
        <CodeSelectInput
          label="Classification"
          value={
            current.type
              ?.flatMap((concept) => concept.coding ?? [])
              .find((coding) => coding.system === APPOINTMENT_TYPE_SYSTEM)
              ?.code ?? NO_CLASSIFICATION
          }
          options={CLASSIFICATION_OPTIONS}
          onChange={(next) => setCurrent(withClassification(current, next))}
        />
        <StringInput
          label="Description"
          value={current.comment}
          placeholder="What patients see when booking"
          onChange={(next) => setCurrent({ ...current, comment: next })}
        />
        <IntegerInput
          label="Duration (minutes)"
          value={parameters.durationMinutes}
          onChange={(next) => setParameter('durationMinutes', next)}
        />
        <IntegerInput
          label="Slot spacing (minutes)"
          value={parameters.alignmentIntervalMinutes}
          onChange={(next) => setParameter('alignmentIntervalMinutes', next)}
        />
        <IntegerInput
          label="Buffer before (minutes)"
          value={parameters.bufferBeforeMinutes}
          onChange={(next) => setParameter('bufferBeforeMinutes', next)}
        />
        <IntegerInput
          label="Buffer after (minutes)"
          value={parameters.bufferAfterMinutes}
          onChange={(next) => setParameter('bufferAfterMinutes', next)}
        />
      </Grid>
    </Stack>
  );
};
